import { DatePicker } from "antd";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useRef, useState } from "react";

import { datePickerPanelCss } from "../../styles";
import {
  DateRangePickerContainer,
  dateRangePickerInputCss,
  dateRangePickerPopupCss,
} from "./DateRangePicker.styles";
import {
  getDateRangeFormat,
  getDateRangePlaceholder,
} from "./dateRangePickerFormat";

// antd parses typed input with dayjs, which needs this plugin to honour the
// display format instead of falling back to Date parsing.
dayjs.extend(customParseFormat);

const { RangePicker } = DatePicker;

export interface IDateRange {
  startDate: Date | null;
  endDate: Date | null;
}

export interface IDateRangePickerProps {
  /** Seeds the picker when it opens; the picker owns the range after that. */
  initialDateRange?: IDateRange;
  /** Fires only once both ends are set, or when the range is cleared. */
  onDateChange?: (dateRange: IDateRange) => void;
}

type RangeValue = [Dayjs | null, Dayjs | null];

/**
 * EN-3907: the portal's custom date-range filter.
 *
 * Replaces the react-datepicker component that hard-coded `disabled` on its two
 * text fields, so a date could only ever be clicked, never typed. antd's
 * RangePicker gives typing, strict parsing, `start <= end` and keyboard support
 * for free rather than through the hand-rolled mask/parse this used to carry.
 *
 * The panel is rendered *inside* this component's own container rather than
 * portalled to `<body>`, for two reasons: it reproduces the previous layout
 * (date fields above, calendar below), and it keeps the panel inside the call
 * sites' popovers so their existing click-outside handlers still see it as an
 * inside click.
 *
 * Dates are emitted at local midnight, matching what react-datepicker produced.
 * Call sites that want the whole end day apply `endOf("day")` themselves, and
 * some deliberately do not — moving that in here would change what they query.
 */
export function DateRangePicker({
  initialDateRange,
  onDateChange,
}: IDateRangePickerProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const format = getDateRangeFormat(navigator.language);

  const [range, setRange] = useState<RangeValue>(() => [
    initialDateRange?.startDate ? dayjs(initialDateRange.startDate) : null,
    initialDateRange?.endDate ? dayjs(initialDateRange.endDate) : null,
  ]);

  const handleChange = (dates: RangeValue | null) => {
    const [start, end] = dates ?? [null, null];

    setRange([start ?? null, end ?? null]);

    if (!onDateChange) {
      return;
    }

    onDateChange({
      startDate: start ? start.startOf("day").toDate() : null,
      endDate: end ? end.startOf("day").toDate() : null,
    });
  };

  return (
    <DateRangePickerContainer ref={containerRef}>
      <RangePicker
        allowClear
        className={dateRangePickerInputCss()}
        classNames={{
          // The shared panel theme first, this picker's own layout second, so the layout rules
          // win where the two touch the same property.
          popup: {
            root: `${datePickerPanelCss()} ${dateRangePickerPopupCss()}`,
          },
        }}
        format={format}
        getPopupContainer={() => containerRef.current ?? document.body}
        onChange={handleChange}
        open
        placeholder={getDateRangePlaceholder(navigator.language)}
        separator="-"
        suffixIcon={null}
        value={range}
      />
    </DateRangePickerContainer>
  );
}
